
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { router } from "@/config/routes";
import { initializeSuperAdmin } from "@/utils/initSuperAdmin";
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { supabase } from "@/integrations/supabase/client";
import { ErrorBoundary } from "react-error-boundary";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

initializeSuperAdmin();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const logError = async () => {
      try {
        const { error: logError } = await supabase.from('monitoring_logs').insert({
          type: 'error',
          description: `Frontend Error: ${error.message}\n${error.stack || 'No stack trace available'}`
        });
        
        if (logError) {
          console.error('Failed to log error:', logError);
        }
      } catch (err) {
        console.error('Failed to log error:', err);
      }
    };
    
    logError();
  }, [error]);

  const handleRetry = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.location.href = '/login';
        return;
      }
      resetErrorBoundary();
    } catch (err) {
      console.error('Error during retry:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-background">
      <div className="text-center space-y-4 p-6 bg-card rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-xl font-semibold text-foreground">Oops! Algo deu errado</h2>
        <p className="text-sm text-muted-foreground">Por favor, tente atualizar a página</p>
        <pre className="text-sm text-destructive bg-destructive/10 p-4 rounded overflow-auto max-h-40">
          {error.message}
        </pre>
        <button
          onClick={handleRetry}
          disabled={isLoading}
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center w-full gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando...
            </>
          ) : (
            'Tentar Novamente'
          )}
        </button>
      </div>
    </div>
  );
}

const App = () => {
  return (
    <SessionContextProvider supabaseClient={supabase}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <TooltipProvider>
            <RouterProvider router={router} />
            <Toaster />
            <Sonner />
          </TooltipProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </SessionContextProvider>
  );
};

export default App;
